import React from "react";
import { PropTypes } from "prop-types";

import basicFetch from "../../utils/basicFetch";
import playerLoader from "../../utils/playerLoader";
import { getPlayerAuthTokenDelegate } from "../../utils/playerAuthToken";

import style from "./PlayerShadowDom.module.scss";

/**
 * Testing version of Player2ShadowDom with additional controls for LTI Basic Outcomes.
 *
 * Usage :
 * ```js
 * <Player2ShadowDomTesting
 *   size='normal'
 *   playerOptions={playerOptions}
 * />
 * ```
 */

class Player2ShadowDomTesting extends React.Component {
  player = null;
  playerContainer = null;
  version = process.env.REACT_APP_PLAYER_2_VERSION;
  stage = process.env.REACT_APP_PLAYER_2_ENV;

  state = {
    testScoreValue: 0.85, // Default test score value
    retrievedScore: null, // Will store the score fetched from the API
    apiResponse: null, // Will store the raw API response
    showApiResponse: false // Controls visibility of the API response details
  }

  async componentDidMount() {
    const asset_manifest = await basicFetch(
      `https://dev.vivedlearning.com/v2/${this.stage}/${this.version}/asset-manifest.json`,
      {
        method: "get",
      }
    );

    const shadow = this.buildShadow();

    let entrypoints = asset_manifest.entrypoints.map((path) => {
      return `https://dev.vivedlearning.com/v2/${this.stage}/${this.version}/${path}`;
    });
    await playerLoader(entrypoints, shadow);

    this.startPlayer();
  }

  buildShadow() {
    let shadowParent = document.getElementById("player-2-shadow");
    let shadow = shadowParent.attachShadow({ mode: "open" });

    shadow.innerHTML = `
    <link type="text/css" href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
    <style> .player-container{ display: flex; position: relative; height: 100%; width: 100%; } </style>
    <div id="player-container-authoring" class="player-container" />
    `;

    this.playerContainer = shadow.getElementById("player-container-authoring");

    shadow.createElement = (...args) => document.createElement(...args);
    shadow.createElementNS = (...args) => document.createElementNS(...args);
    shadow.createTextNode = (...args) => document.createTextNode(...args);

    return shadow;
  }

  startPlayer() {
    // The player self-signs from this id (it fetches the signing secret
    // itself) whenever no auth delegate is attached — anonymous viewers and
    // LTI launches. Signed-in, non-LTI users get the user-token delegate; a
    // mount site may also supply its own delegate through playerOptions.
    let options = {
      playerId: "9edccc93-bce4-40ed-a716-6b58294c8d99",
      ...this.props.playerOptions,
    };

    const getAuthToken =
      this.props.playerOptions?.getAuthToken ?? getPlayerAuthTokenDelegate();
    if (getAuthToken) {
      options.getAuthToken = getAuthToken;
    }

    this.player = window.vived_player.mount(this.playerContainer, options);
  }

  componentWillUnmount() {
    if (this.player) {
      this.player.dispose();
    }

    if (this.playerContainer) {
      window.vived_player.unmount(this.playerContainer);
    }
  }

  handleTestScoreSubmit = () => {
    if (this.props.playerOptions?.postActivityScore) {
      console.log(`[DEV] Submitting test score: ${this.state.testScoreValue}`);
      this.props.playerOptions.postActivityScore(this.state.testScoreValue)
        .then(response => {
          console.log('[DEV] Score submission response:', response);
          alert(`Score ${this.state.testScoreValue} submitted successfully!`);
        })
        .catch(error => {
          console.error('[DEV] Score submission error:', error);
          alert(`Error submitting score: ${error.message}`);
        });
    } else {
      console.warn('[DEV] postActivityScore function not available');
      alert('postActivityScore function not available');
    }
  }

  handleTestScoreChange = (e) => {
    const value = parseFloat(e.target.value);
    this.setState({ testScoreValue: value });
  }

  handleFetchPreviousScore = () => {
    if (this.props.playerOptions?.fetchPreviousActivityScore) {
      console.log('[DEV] Fetching previous activity score');
      this.props.playerOptions.fetchPreviousActivityScore()
        .then(response => {
          console.log('[DEV] Previous score fetch response:', response);
          // Extract the score from the response if it exists
          const score = response?.score !== undefined ? response.score : null;
          this.setState({
            retrievedScore: score,
            apiResponse: response,
            showApiResponse: true
          });
        })
        .catch(error => {
          console.error('[DEV] Previous score fetch error:', error);
          alert(`Error fetching previous score: ${error.message}`);
          this.setState({ retrievedScore: null, apiResponse: null });
        });
    } else {
      console.warn('[DEV] fetchPreviousActivityScore function not available');
      alert('fetchPreviousActivityScore function not available');
    }
  }

  toggleApiResponseVisibility = () => {
    this.setState(prevState => ({ showApiResponse: !prevState.showApiResponse }));
  }

  renderDevTools() {
    // Check if LTI functionality is available
    if (!this.props.playerOptions?.postActivityScore && !this.props.playerOptions?.fetchPreviousActivityScore) {
      return null;
    }

    const { retrievedScore, apiResponse, showApiResponse } = this.state;
    const hasRetrievedScore = retrievedScore !== null;

    const containerStyle = {
      position: 'absolute',
      top: '10px',
      right: '10px',
      zIndex: 1000,
      background: 'rgba(255, 255, 100, 0.9)',
      padding: '10px',
      border: '2px solid #ff6600',
      borderRadius: '5px',
      boxShadow: '0 0 10px rgba(0,0,0,0.5)',
      maxWidth: '350px'
    };

    const buttonStyle = {
      padding: '5px 10px',
      background: '#ff6600',
      color: 'white',
      border: 'none',
      borderRadius: '3px',
      cursor: 'pointer',
      fontWeight: 'bold',
      marginRight: '5px'
    };

    const secondaryButtonStyle = {
      ...buttonStyle,
      background: '#888'
    };

    const scoreDisplayStyle = {
      marginTop: '10px',
      padding: '8px',
      background: 'rgba(255, 255, 255, 0.8)',
      border: '1px solid #ccc',
      borderRadius: '3px'
    };

    const jsonDisplayStyle = {
      marginTop: '10px',
      padding: '8px',
      background: 'rgba(0, 0, 0, 0.7)',
      color: '#00ff00',
      border: '1px solid #444',
      borderRadius: '3px',
      fontFamily: 'monospace',
      fontSize: '12px',
      whiteSpace: 'pre-wrap',
      maxHeight: '200px',
      overflowY: 'auto'
    };

    return (
      <div style={containerStyle}>
        <div style={{marginBottom: '10px', fontWeight: 'bold'}}>LTI OUTCOME TEST</div>

        {/* Score input and submission controls */}
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
          <label style={{marginRight: '10px'}}>Score (0-1):</label>
          <input
            type="number"
            min="0"
            max="1"
            step="0.01"
            value={this.state.testScoreValue}
            onChange={this.handleTestScoreChange}
            style={{width: '70px'}}
          />
        </div>

        {/* Action buttons */}
        <div style={{display: 'flex', marginBottom: '10px'}}>
          <button
            onClick={this.handleTestScoreSubmit}
            style={buttonStyle}
          >
            Submit Score
          </button>

          {this.props.playerOptions?.fetchPreviousActivityScore && (
            <button
              onClick={this.handleFetchPreviousScore}
              style={{...buttonStyle, marginLeft: '10px'}}
            >
              Read Current Score
            </button>
          )}
        </div>

        {/* Retrieved score display */}
        {hasRetrievedScore && (
          <div style={scoreDisplayStyle}>
            <div><strong>Retrieved Score:</strong> {retrievedScore}</div>
          </div>
        )}

        {/* API Response toggle and display */}
        {apiResponse && (
          <div style={{marginTop: '10px'}}>
            <button
              onClick={this.toggleApiResponseVisibility}
              style={secondaryButtonStyle}
            >
              {showApiResponse ? 'Hide API Response' : 'Show API Response'}
            </button>

            {showApiResponse && (
              <div style={jsonDisplayStyle}>
                {JSON.stringify(apiResponse, null, 2)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  render() {
    const classes = [
      style.container,
      this.props.size === "normal" ? style.containerNormal : null,
      this.props.size === "freeplay" ? style.containerFreeplayFullsize : null,
      this.props.size === "authoring" ? style.containerAuthoring : null,
      this.props.size === "embed" ? style.containerEmbed : null,
      this.props.size === "fill" ? style.containerFill : null,
    ].join(" ");

    return (
      <div style={{ position: 'relative' }}>
        {this.renderDevTools()}
        <div id="player-2-shadow" className={classes} />
      </div>
    );
  }
}

Player2ShadowDomTesting.defaultProps = {
  appBar: false,
};

Player2ShadowDomTesting.propTypes = {
  appBar: PropTypes.bool,
  playerOptions: PropTypes.any,
  size: PropTypes.oneOf(["normal", "freeplay", "authoring", "embed", "fill"])
    .isRequired,
  title: PropTypes.string,
};

export default Player2ShadowDomTesting;
